import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Boxes, Loader2, Sparkles, CheckCircle2, XCircle, MinusCircle,
  Play, Gauge, Target, ListChecks, Rocket, Wand2, Layers, ChevronRight, Circle,
  Cpu, ShieldCheck, Repeat, Infinity as InfinityIcon, Activity, Users, MapPin, RotateCcw, Redo2, Bug, Eye,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Spinner } from "@/components/Bits";
import GameCanvas from "@/components/GameCanvas";
import DepthJourneyCard from "@/components/DepthJourneyCard";
import PlaytestReport from "@/components/PlaytestReport";
import VisualQA from "@/components/VisualQA";
import ShipIt from "@/components/ShipIt";
import GenreBenchmarks from "@/components/GenreBenchmarks";
import ClimbNudge from "@/components/ClimbNudge";
import AutoDirector from "@/components/AutoDirector";
import { toast } from "sonner";

const ZONE_KINDS = [
  { id: "village", label: "Village", emoji: "🏘️" },
  { id: "market", label: "Market", emoji: "🛒" },
  { id: "camp", label: "Camp", emoji: "⛺" },
  { id: "ruins", label: "Ruins", emoji: "🏛️" },
  { id: "shrine", label: "Shrine", emoji: "⛩️" },
  { id: "outpost", label: "Outpost", emoji: "🏰" },
  { id: "tavern", label: "Tavern", emoji: "🍺" },
  { id: "harbor", label: "Harbor", emoji: "⚓" },
];

const PRIO = {
  core: { label: "Core", color: "#f43f5e" },
  major: { label: "Major", color: "#f59e0b" },
  polish: { label: "Polish", color: "#a855f7" },
};

const STATE_COLOR = (s = "") => {
  const k = s.toLowerCase();
  if (k.startsWith("release")) return "#10b981";
  if (k.startsWith("beta")) return "#3b9eff";
  if (k.startsWith("alpha")) return "#f59e0b";
  return "#94a3b8"; // prototype
};

const barColor = (pct) => (pct >= 85 ? "#10b981" : pct >= 40 ? "#3b9eff" : pct >= 1 ? "#f59e0b" : "#64748b");

export default function GameCompiler() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [config, setConfig] = useState(null);
  const [modes, setModes] = useState([]);
  const [mode, setMode] = useState("full");
  const [blueprint, setBlueprint] = useState(null);
  const [depth, setDepth] = useState(null);
  const [compiling, setCompiling] = useState(false);
  const [depthBusy, setDepthBusy] = useState(false);
  const [run, setRun] = useState(null);          // {status, planned[], steps[], current, total, built, depth_after}
  const [running, setRunning] = useState(false);
  const [deep, setDeep] = useState(true);         // Phase 2 — deep content families
  const [critic, setCritic] = useState(true);     // Phase 3 — Quality Director
  const [playtesters, setPlaytesters] = useState(false); // Phase 5 — Playtester Army
  const [selfdebug, setSelfdebug] = useState(false);  // Phase 5 — Self-Debug Loop
  const [autopass, setAutopass] = useState(false);    // Auto Art Pass — auto-apply top visual fix
  const [auto, setAuto] = useState(false);        // Vision Autopilot
  const [target, setTarget] = useState(75);
  const [runs, setRuns] = useState([]);
  const [boardTotals, setBoardTotals] = useState(null);
  const [openRun, setOpenRun] = useState(null);
  const [confirmUndo, setConfirmUndo] = useState(null);
  const [undoing, setUndoing] = useState(false);
  const [zones, setZones] = useState([]);
  const [zonePlace, setZonePlace] = useState("");
  const [zoneKind, setZoneKind] = useState("village");
  const [zoneBusy, setZoneBusy] = useState(false);
  const [pinMode, setPinMode] = useState(false);     // Zone On The Map — drop-a-pin placement
  const [zonePin, setZonePin] = useState(null);       // {x,y,z} from the 3D world
  const [redoing, setRedoing] = useState(false);
  const canvasRef = useRef(null);
  const visualQARef = useRef(null);
  const shipItRef = useRef(null);
  const autoRunRef = useRef(false);
  const autoBuildRef = useRef(false);
  const [searchParams] = useSearchParams();
  const autoCompiledRef = useRef(false);
  const pollRef = useRef(null);

  useEffect(() => {
    api.get(`/games/${gameId}`).then(({ data }) => {
      setGame(data);
      setConfig(data.config);
    }).catch(() => toast.error("Couldn't open this game."));
    api.get(`/compiler/modes`).then(({ data }) => setModes(data.modes || [])).catch(() => {});
    return () => clearInterval(pollRef.current);
  }, [gameId]);

  const refreshDepth = useCallback(async (m) => {
    setDepthBusy(true);
    try {
      const { data } = await api.get(`/compiler/${gameId}/depth`, { params: { mode: m } });
      setDepth(data);
    } catch { /* keep old */ }
    setDepthBusy(false);
  }, [gameId]);

  const fetchRuns = useCallback(async () => {
    try {
      const { data } = await api.get(`/compiler/${gameId}/runs`);
      setRuns(data.runs || []);
      setBoardTotals(data.totals || null);
    } catch { /* ignore */ }
  }, [gameId]);

  // Load stored blueprint + live depth
  useEffect(() => {
    if (!game) return;
    api.get(`/compiler/${gameId}/blueprint`).then(({ data }) => {
      setBlueprint(data.blueprint || null);
      setMode(data.mode || "full");
      setDepth(data.depth || null);
      setZones(data.zones || []);
    }).catch(() => {});
    fetchRuns();
  }, [game, gameId, fetchRuns]);

  const changeMode = (m) => {
    if (m === mode) return;
    setMode(m);
    refreshDepth(m);
  };

  const compileBlueprint = useCallback(async () => {
    setCompiling(true);
    try {
      const { data } = await api.post(`/compiler/${gameId}/blueprint`, { mode }, { timeout: 120000 });
      setBlueprint(data.blueprint || null);
      setMode(data.mode || mode);
      setDepth(data.depth || null);
      toast.success("Game Blueprint compiled — this is the full vision! 🧭");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "The Game Compiler couldn't draft a blueprint.");
    }
    setCompiling(false);
  }, [gameId, mode]);

  // Compile From Chat: /app/compiler/:id?compile=1 auto-compiles the blueprint on arrival.
  useEffect(() => {
    if (searchParams.get("compile") === "1" && game && !autoCompiledRef.current) {
      autoCompiledRef.current = true;
      compileBlueprint();
    }
  }, [searchParams, game, compileBlueprint]);

  const poll = useCallback((runId) => {
    clearInterval(pollRef.current);
    let ticks = 0;
    const MAX_TICKS = 170; // ~6.2 min guard so the UI never spins forever if a run stalls
    pollRef.current = setInterval(async () => {
      ticks += 1;
      if (ticks > MAX_TICKS) {
        clearInterval(pollRef.current);
        setRunning(false);
        await refreshDepth(mode);
        toast.message("Still building in the background — reopen the Compiler in a moment to see the result.");
        return;
      }
      try {
        const { data } = await api.get(`/compiler/${gameId}/complete-vision/${runId}`);
        setRun(data);
        if (data.status !== "processing") {
          clearInterval(pollRef.current);
          setRunning(false);
          if (data.config) { setConfig(data.config); setGame((g) => (g ? { ...g, config: data.config } : g)); }
          await refreshDepth(mode);
          api.get(`/compiler/${gameId}/blueprint`).then(({ data: b }) => setBlueprint(b.blueprint || null)).catch(() => {});
          fetchRuns();
          if (data.status === "done") {
            toast.success(`Vision advanced — built ${data.built} system${data.built === 1 ? "" : "s"}! 🚀`);
            // Autonomous Art Director: once the rebuilt preview has settled, auto-scan the visuals.
            if (data.built > 0) setTimeout(() => { try { visualQARef.current?.run?.({ autoApplyTop: autopass }); } catch { /* ignore */ } }, 4800);
            // Auto Ship Check: when a Vision Autopilot run finishes, run the release gate & celebrate.
            if (autoRunRef.current) {
              autoRunRef.current = false;
              setTimeout(() => { try { shipItRef.current?.checkAndCelebrate?.(); } catch { /* ignore */ } }, data.built > 0 ? 7000 : 800);
            }
          } else {
            toast.message("That build was stopped — your saved progress is safe. You can run it again anytime.");
          }
        }
      } catch {
        clearInterval(pollRef.current);
        setRunning(false);
      }
    }, 2200);
  }, [gameId, mode, refreshDepth, autopass]);

  const completeVision = async (boost = false) => {
    setRunning(true);
    setRun(null);
    autoRunRef.current = !!auto;   // Auto Ship Check fires only for Vision Autopilot runs
    try {
      const { data } = await api.post(`/compiler/${gameId}/complete-vision`,
        { mode, boost, max_actions: 6, deep, critic, playtesters, selfdebug, auto, target: auto ? target : undefined },
        { timeout: 60000 });
      if (data.status === "complete" || !data.run_id) {
        setRunning(false);
        autoRunRef.current = false;
        toast.success(data.message || "Your game already meets this vision — nothing left to build!");
        return;
      }
      if (data.mode && data.mode !== mode) setMode(data.mode);
      setRun({ status: "processing", planned: data.planned || [], steps: [], current: 0, total: data.total || 0, built: 0, auto: data.auto, target: data.target });
      poll(data.run_id);
      fetchRuns();
    } catch (e) {
      setRunning(false);
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start the build — try again.");
    }
  };

  // Weakest-First Build: /app/compiler/:id?autobuild=1 auto-starts a build on arrival (one-tap
  // "Improve" from the Genre Benchmarks board lands here).
  useEffect(() => {
    if (searchParams.get("autobuild") === "1" && game && depth && !autoBuildRef.current && !running) {
      autoBuildRef.current = true;
      toast.message("Auto-building your weakest game to level it up… 🔧");
      completeVision(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, game, depth]);

  const togglePinMode = () => {
    const next = !pinMode;
    setPinMode(next);
    try { canvasRef.current?.zonePlaceMode(next); } catch { /* ignore */ }
    if (next) toast.message("Tap the ground in the live preview to drop your zone pin 📍");
  };

  const handleZonePin = (pos) => {
    if (!pos) return;
    setZonePin(pos);
    setPinMode(false);
    toast.success(`Pin dropped at (${Math.round(pos.x)}, ${Math.round(pos.z)}) — now build your place here.`);
  };

  const clearPin = () => {
    setZonePin(null);
    setPinMode(false);
    try { canvasRef.current?.zonePlaceMode(false); } catch { /* ignore */ }
  };

  const handleVisualApplied = (cfg) => {
    setConfig(cfg);
    setGame((g) => (g ? { ...g, config: cfg } : g));
    refreshDepth(mode);
    fetchRuns();
  };

  const buildZone = async () => {
    const place = zonePlace.trim();
    if (place.length < 2) { toast.error("Name the place you want to build."); return; }
    setZoneBusy(true);
    try {
      const { data } = await api.post(`/compiler/${gameId}/zone`, { place, kind: zoneKind, pos: zonePin || undefined }, { timeout: 120000 });
      if (data.config) { setConfig(data.config); setGame((g) => (g ? { ...g, config: data.config } : g)); }
      if (data.zone) setZones((z) => [...z, data.zone]);
      setZonePlace("");
      setZonePin(null);
      await refreshDepth(mode);
      fetchRuns();
      toast.success(data.summary || `Built ${place}! 🗺️`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Creation Zones couldn't build that place.");
    }
    setZoneBusy(false);
  };

  const undoRun = async (runId) => {
    setUndoing(true);
    try {
      const { data } = await api.post(`/compiler/${gameId}/runs/${runId}/undo`, {}, { timeout: 30000 });
      if (data.config) { setConfig(data.config); setGame((g) => (g ? { ...g, config: data.config } : g)); }
      await refreshDepth(mode);
      api.get(`/compiler/${gameId}/blueprint`).then(({ data: b }) => setBlueprint(b.blueprint || null)).catch(() => {});
      fetchRuns();
      setConfirmUndo(null);
      toast.success("Reverted that run — your game is back to how it was before it. ↩️");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't undo that run.");
    }
    setUndoing(false);
  };

  const redoRun = async (runId) => {
    setRedoing(true);
    try {
      const { data } = await api.post(`/compiler/${gameId}/runs/${runId}/redo`, {}, { timeout: 30000 });
      if (data.config) { setConfig(data.config); setGame((g) => (g ? { ...g, config: data.config } : g)); }
      await refreshDepth(mode);
      api.get(`/compiler/${gameId}/blueprint`).then(({ data: b }) => setBlueprint(b.blueprint || null)).catch(() => {});
      fetchRuns();
      toast.success("Re-applied that run — your game is back to how it was after it. ↪️");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't redo that run.");
    }
    setRedoing(false);
  };

  if (!game) return <div className="min-h-[60vh] grid place-items-center"><Spinner /></div>;

  const overall = depth?.overall ?? 0;
  const sections = depth?.sections || [];
  const journeyBefore = runs.length ? (runs[runs.length - 1].depth_before ?? 0) : 0;
  const stepByKey = {};
  (run?.steps || []).forEach((s) => { stepByKey[s.key] = s; });

  return (
    <div className="max-w-6xl mx-auto px-4 py-6" data-testid="compiler-studio">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/app/build/${gameId}`)} data-testid="compiler-back">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="w-11 h-11 rounded-2xl grid place-items-center bg-gradient-to-br from-[#0ea5e9] to-[#7c3aed] shrink-0">
          <Boxes className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0">
          <h1 className="font-display text-xl font-extrabold leading-tight truncate">Game Compiler</h1>
          <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">
            Compile <b>{game.title}</b> into its full vision — then complete every missing system.
          </p>
        </div>
      </div>

      {/* Mode picker */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="compiler-modes">
        {(modes.length ? modes : [{ id: "prototype", label: "Prototype" }, { id: "playable", label: "Playable" }, { id: "full", label: "Full Game" }, { id: "studio", label: "Studio Quality" }]).map((m) => (
          <button key={m.id} onClick={() => changeMode(m.id)} data-testid={`compiler-mode-${m.id}`}
            title={m.desc || ""}
            className={`rounded-full px-3.5 py-1.5 text-sm font-semibold border transition-colors ${mode === m.id ? "border-transparent bg-gradient-to-r from-[#0ea5e9] to-[#7c3aed] text-white" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:border-[hsl(var(--primary))]/50"}`}>
            {m.label}
          </button>
        ))}
        {depthBusy && <span className="inline-flex items-center text-xs text-[hsl(var(--muted-foreground))]"><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> recalculating…</span>}
      </div>

      <div className="grid lg:grid-cols-2 gap-6 items-start">
        {/* LEFT */}
        <div className="space-y-5">
          {/* Blueprint */}
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5" data-testid="compiler-blueprint">
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2 text-sm font-bold"><Wand2 className="w-4 h-4 text-[hsl(var(--primary))]" /> Game Blueprint</div>
              <Button size="sm" variant={blueprint ? "outline" : "default"} onClick={compileBlueprint} disabled={compiling}
                className={`rounded-full text-xs ${blueprint ? "" : "bg-gradient-to-r from-[#0ea5e9] to-[#7c3aed]"}`} data-testid="compiler-compile-btn">
                {compiling ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Compiling…</>
                  : blueprint ? <><Sparkles className="w-3.5 h-3.5" /> Refresh</>
                    : <><Sparkles className="w-3.5 h-3.5" /> Compile vision <span className="opacity-70">· 2 cr</span></>}
              </Button>
            </div>

            {!blueprint ? (
              <p className="text-sm text-[hsl(var(--muted-foreground))] leading-snug">
                Compile your idea into a complete <b>Game Blueprint</b> — the AI reads it as a full software project
                and maps every system a game like this truly needs (world, hero, enemies, boss, quests, items, and more).
              </p>
            ) : (
              <div className="space-y-3">
                <p className="text-sm font-medium leading-snug" data-testid="compiler-blueprint-concept">{blueprint.concept}</p>
                <div className="flex flex-wrap gap-1.5">
                  {blueprint.genre && <Chip>{blueprint.genre}</Chip>}
                  {blueprint.perspective && <Chip>{blueprint.perspective}</Chip>}
                </div>
                {Array.isArray(blueprint.core_loop) && blueprint.core_loop.length > 0 && (
                  <div className="flex items-center flex-wrap gap-1 text-[11px] text-[hsl(var(--muted-foreground))]">
                    {blueprint.core_loop.map((v, i) => (
                      <span key={i} className="inline-flex items-center">
                        <span className="font-semibold text-[hsl(var(--foreground))]">{v}</span>
                        {i < blueprint.core_loop.length - 1 && <ChevronRight className="w-3 h-3 mx-0.5 opacity-50" />}
                      </span>
                    ))}
                  </div>
                )}
                {blueprint.art_direction && <p className="text-[12px] text-[hsl(var(--muted-foreground))] italic leading-snug">🎨 {blueprint.art_direction}</p>}

                {Array.isArray(blueprint.systems) && blueprint.systems.length > 0 && (
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1.5">Systems the vision needs</div>
                    <div className="flex flex-wrap gap-1.5">
                      {blueprint.systems.map((s) => {
                        const p = PRIO[(s.priority || "major")] || PRIO.major;
                        return (
                          <span key={s.key} data-testid={`compiler-system-${s.key}`} title={s.why || ""}
                            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium border"
                            style={{ borderColor: `${p.color}55`, color: p.color, background: `${p.color}12` }}>
                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: p.color }} /> {s.key}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
                {Array.isArray(blueprint.highlights) && blueprint.highlights.length > 0 && (
                  <ul className="text-[12px] text-[hsl(var(--muted-foreground))] space-y-1 mt-1">
                    {blueprint.highlights.map((h, i) => (
                      <li key={i} className="flex items-start gap-1.5"><Sparkles className="w-3 h-3 mt-0.5 text-[hsl(var(--primary))] shrink-0" /> {h}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          <ClimbNudge gameId={gameId} />

          {/* Creation Depth Report */}
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5" data-testid="compiler-depth">
            <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
              <div className="flex items-center gap-2 text-sm font-bold"><Gauge className="w-4 h-4 text-[hsl(var(--primary))]" /> Creation Depth</div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <AutoDirector
                  gameId={gameId}
                  capture={() => canvasRef.current?.capture()}
                  title={config.title}
                  onConfig={(cfg) => { setConfig(cfg); setGame((g) => (g ? { ...g, config: cfg } : g)); }}
                  refresh={() => { refreshDepth(mode); api.get(`/compiler/${gameId}/blueprint`).then(({ data: b }) => setBlueprint(b.blueprint || null)).catch(() => {}); fetchRuns(); }}
                />
                <GenreBenchmarks />
                <ShipIt ref={shipItRef} gameId={gameId} title={config.title} genre={blueprint?.genre} />
                <PlaytestReport gameId={gameId} title={config.title} genre={blueprint?.genre} />
                <DepthJourneyCard gameId={gameId} title={config.title} depth={depth} genre={blueprint?.genre} journeyBefore={journeyBefore} />
              </div>
            </div>
            <div className="flex items-end gap-4 mb-4">
              <div>
                <div className="text-4xl font-extrabold leading-none" data-testid="compiler-depth-overall">{overall}<span className="text-lg text-[hsl(var(--muted-foreground))]">%</span></div>
                <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">of the vision built</div>
              </div>
              <div className="flex-1 pb-1">
                <div className="h-2 rounded-full bg-[hsl(var(--secondary))] overflow-hidden">
                  <div className="h-full transition-all duration-700" style={{ width: `${overall}%`, background: `linear-gradient(90deg, #0ea5e9, #7c3aed)` }} />
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5"
                    style={{ color: STATE_COLOR(depth?.state), background: `${STATE_COLOR(depth?.state)}18` }} data-testid="compiler-depth-state">
                    {depth?.state || "—"}
                  </span>
                  {depth && <span className="text-[11px] text-[hsl(var(--muted-foreground))]">{depth.built}/{depth.total} systems complete</span>}
                </div>
              </div>
            </div>

            <div className="space-y-2" data-testid="compiler-sections">
              {sections.map((s) => {
                const st = stepByKey[s.key];
                return (
                  <div key={s.key} className="rounded-xl border border-[hsl(var(--border))] px-3 py-2" data-testid={`compiler-section-${s.key}`}>
                    <div className="flex items-center gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-medium truncate">{s.label}</span>
                          {st?.status === "built" && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />}
                        </div>
                        <div className="h-1.5 rounded-full bg-[hsl(var(--secondary))] overflow-hidden mt-1">
                          <div className="h-full transition-all duration-500" style={{ width: `${s.pct}%`, background: barColor(s.pct) }} />
                        </div>
                      </div>
                      <div className="text-right shrink-0 w-16">
                        {s.missing > 0
                          ? <span className="text-[11px] font-semibold text-[hsl(var(--muted-foreground))]">+{s.missing} to add</span>
                          : <span className="text-[11px] font-semibold text-emerald-500">✓ done</span>}
                        <div className="text-[10px] text-[hsl(var(--muted-foreground))]">{s.have}/{s.target || "—"}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Complete the Vision */}
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
            <div className="flex items-center gap-2 mb-2 text-sm font-bold"><Target className="w-4 h-4 text-[hsl(var(--primary))]" /> Complete The Vision</div>
            <p className="text-[12px] text-[hsl(var(--muted-foreground))] leading-snug mb-4">
              The Compiler builds the biggest missing systems for you — each as an <b>undoable version</b> you can tweak later.
            </p>

            {/* Engine options */}
            <div className="space-y-2.5 mb-4" data-testid="compiler-options">
              <OptionRow icon={Cpu} title="Deep Generation" desc="Expand each system into a whole content family in one pass"
                checked={deep} onChange={setDeep} testid="compiler-opt-deep" disabled={running} />
              <OptionRow icon={ShieldCheck} title="Quality Director" desc="Review every built system and auto-fix weak spots"
                checked={critic} onChange={setCritic} testid="compiler-opt-critic" disabled={running || playtesters} />
              <OptionRow icon={Users} title="Playtester Army" desc="3 AI playtesters stress-play each system and feed fixes to the QD"
                checked={playtesters} onChange={setPlaytesters} testid="compiler-opt-playtesters" disabled={running} />
              <OptionRow icon={Bug} title="Self-Debug Loop" desc="Review → fix → re-review each system until it passes (up to 3 rounds)"
                checked={selfdebug} onChange={setSelfdebug} testid="compiler-opt-selfdebug" disabled={running || (!critic && !playtesters)} />
              <OptionRow icon={Eye} title="Auto Art Pass" desc="After each build, the Art Director auto-applies the top visual upgrade"
                checked={autopass} onChange={setAutopass} testid="compiler-opt-autopass" disabled={running} />
              <OptionRow icon={Repeat} title="Vision Autopilot" desc="Keep building on its own until the depth target is hit"
                checked={auto} onChange={setAuto} testid="compiler-opt-auto" disabled={running} />
              {auto && (
                <div className="pl-8" data-testid="compiler-target">
                  <div className="text-[11px] font-semibold text-[hsl(var(--muted-foreground))] mb-1.5">Build until depth reaches</div>
                  <div className="flex gap-1.5">
                    {[60, 75, 90, 100].map((t) => (
                      <button key={t} onClick={() => setTarget(t)} disabled={running} data-testid={`compiler-target-${t}`}
                        className={`rounded-full px-3 py-1 text-xs font-semibold border transition-colors ${target === t ? "border-transparent bg-gradient-to-r from-[#0ea5e9] to-[#7c3aed] text-white" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"}`}>
                        {t === 100 ? <span className="inline-flex items-center gap-0.5"><InfinityIcon className="w-3 h-3" />100%</span> : `${t}%`}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <Button onClick={() => completeVision(false)} disabled={running || compiling}
                className="rounded-full flex-1 bg-gradient-to-r from-[#0ea5e9] to-[#7c3aed]" data-testid="compiler-complete-btn">
                {running ? <><Loader2 className="w-4 h-4 animate-spin" /> Building…</>
                  : auto ? <><Repeat className="w-4 h-4" /> Autopilot to {target}%</>
                    : <><Rocket className="w-4 h-4" /> Complete the vision</>}
              </Button>
              <Button variant="outline" onClick={() => completeVision(true)} disabled={running || compiling}
                className="rounded-full shrink-0" title="Step up a depth level and build even deeper" data-testid="compiler-boost-btn">
                <Layers className="w-4 h-4" /> Build it properly
              </Button>
            </div>

            {run && (
              <div className="mt-4" data-testid="compiler-run">
                <div className="h-1.5 rounded-full bg-[hsl(var(--secondary))] overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#0ea5e9] to-[#7c3aed] transition-all duration-500"
                    style={{ width: `${run.total ? Math.min(100, ((run.steps?.length || 0) / run.total) * 100) : 0}%` }} />
                </div>
                <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1.5">
                  {run.status === "done" ? `Done — built ${run.built}/${run.total}${run.passes > 1 ? ` over ${run.passes} passes` : ""}`
                    : `Building… ${run.steps?.length || 0} / ${run.total}${run.auto ? ` · autopilot to ${run.target || target}%` : ""}`}
                </p>
                <div className="space-y-1.5 mt-2">
                  {(run.planned || []).map((p, i) => {
                    const st = (run.steps || [])[i];
                    const active = running && (run.steps?.length || 0) === i;
                    return (
                      <div key={p.key + i} className="rounded-lg border border-[hsl(var(--border))]/60 px-2.5 py-1.5" data-testid={`compiler-run-step-${i}`}>
                        <div className="flex items-center gap-2 text-sm">
                          {st?.status === "built" ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                            : st?.status === "failed" ? <XCircle className="w-4 h-4 text-rose-500 shrink-0" />
                              : st?.status === "skipped" ? <MinusCircle className="w-4 h-4 text-[hsl(var(--muted-foreground))] shrink-0" />
                                : active ? <Loader2 className="w-4 h-4 animate-spin text-[hsl(var(--primary))] shrink-0" />
                                  : <Circle className="w-4 h-4 text-[hsl(var(--muted-foreground))]/40 shrink-0" />}
                          <span className={`truncate ${st?.status === "built" ? "font-medium" : "text-[hsl(var(--muted-foreground))]"}`}>{p.label}</span>
                          {p.pass > 1 && <span className="text-[9px] font-bold uppercase rounded px-1 py-0.5 bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))] shrink-0">pass {p.pass}</span>}
                          {st?.engine === "deep" && <span className="text-[9px] font-bold uppercase rounded px-1 py-0.5 bg-[#0ea5e9]/15 text-[#0ea5e9] shrink-0">deep</span>}
                          {st?.version != null && <span className="text-[10px] text-emerald-500 font-semibold ml-auto shrink-0">v{st.version}</span>}
                        </div>
                        {st?.summary && (st.status === "built") && (
                          <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5 leading-snug pl-6">{st.summary}</p>
                        )}
                        {st?.critic && (
                          <div className="mt-1 pl-6 flex items-start gap-1.5" data-testid={`compiler-run-critic-${i}`}>
                            {st.critic.mode === "playtest" ? <Users className="w-3 h-3 mt-0.5 shrink-0" style={{ color: st.critic.score >= 70 ? "#10b981" : "#f59e0b" }} />
                              : <ShieldCheck className="w-3 h-3 mt-0.5 shrink-0" style={{ color: st.critic.score >= 70 ? "#10b981" : "#f59e0b" }} />}
                            <div className="min-w-0">
                              <p className="text-[11px] leading-snug" style={{ color: "hsl(var(--muted-foreground))" }}>
                                <b style={{ color: st.critic.score >= 70 ? "#10b981" : "#f59e0b" }}>{st.critic.mode === "playtest" ? "Playtesters" : "QD"} {st.critic.score}</b> — {st.critic.verdict}
                                {st.critic.selfdebug && Array.isArray(st.critic.iterations) && st.critic.iterations.length > 1 && (
                                  <span className="text-[#0ea5e9] font-semibold"> · 🔁 {st.critic.iterations.length} rounds</span>
                                )}
                                {st.critic.fixed && <span className="text-emerald-500 font-semibold"> · auto-fixed ✓</span>}
                              </p>
                              {Array.isArray(st.critic.findings) && st.critic.findings.map((f, fi) => (
                                <p key={fi} className="text-[10px] text-[hsl(var(--muted-foreground))] leading-snug"><b>{f.persona}:</b> {f.note}</p>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Creation Zones */}
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5" data-testid="compiler-zones">
            <div className="flex items-center gap-2 mb-2 text-sm font-bold"><MapPin className="w-4 h-4 text-[hsl(var(--primary))]" /> Creation Zones</div>
            <p className="text-[12px] text-[hsl(var(--muted-foreground))] leading-snug mb-3">
              Name a place and pick what to build there — the Compiler deep-generates a themed cluster (characters, a region and local quests) anchored to it.
              <b className="text-[hsl(var(--foreground))]"> Drop a pin</b> in the live preview to build it at an exact spot, with a landmark players can find.
            </p>

            {/* Zone On The Map — drop-a-pin */}
            <div className="flex items-center gap-2 mb-2.5">
              <Button type="button" size="sm" variant={pinMode ? "default" : "outline"} onClick={togglePinMode} disabled={zoneBusy}
                className={`rounded-full text-xs gap-1.5 ${pinMode ? "bg-gradient-to-r from-[#0ea5e9] to-[#7c3aed]" : ""}`} data-testid="compiler-zone-pin-btn">
                <MapPin className="w-3.5 h-3.5" /> {pinMode ? "Tap the world…" : zonePin ? "Move pin" : "Drop a pin"}
              </Button>
              {zonePin && (
                <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold border border-[#0ea5e9]/40 text-[#0ea5e9] bg-[#0ea5e9]/10" data-testid="compiler-zone-pin-chip">
                  📍 ({Math.round(zonePin.x)}, {Math.round(zonePin.z)})
                  <button onClick={clearPin} className="text-[hsl(var(--muted-foreground))] hover:text-rose-400" data-testid="compiler-zone-pin-clear" title="Clear pin">✕</button>
                </span>
              )}
            </div>

            <Input value={zonePlace} onChange={(e) => setZonePlace(e.target.value)} disabled={zoneBusy}
              placeholder="e.g. a fishing village by the northern lake" maxLength={80}
              className="mb-2.5" data-testid="compiler-zone-place" />
            <div className="flex flex-wrap gap-1.5 mb-3">
              {ZONE_KINDS.map((k) => (
                <button key={k.id} onClick={() => setZoneKind(k.id)} disabled={zoneBusy} data-testid={`compiler-zone-kind-${k.id}`}
                  className={`rounded-full px-2.5 py-1 text-xs font-semibold border transition-colors ${zoneKind === k.id ? "border-transparent bg-gradient-to-r from-[#0ea5e9] to-[#7c3aed] text-white" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"}`}>
                  {k.emoji} {k.label}
                </button>
              ))}
            </div>
            <Button onClick={buildZone} disabled={zoneBusy || zonePlace.trim().length < 2}
              className="rounded-full w-full bg-gradient-to-r from-[#0ea5e9] to-[#7c3aed]" data-testid="compiler-zone-build-btn">
              {zoneBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Building the zone…</>
                : <><MapPin className="w-4 h-4" /> {zonePin ? "Build here" : "Build this place"} <span className="opacity-70 text-xs">· 2 cr</span></>}
            </Button>
            {zones.length > 0 && (
              <div className="mt-3 space-y-1.5" data-testid="compiler-zone-list">
                {zones.slice().reverse().map((z) => (
                  <div key={z.id} className="flex items-center gap-2 rounded-lg border border-[hsl(var(--border))] px-2.5 py-1.5 text-[12px]" data-testid={`compiler-zone-${z.id}`}>
                    <span className="text-base shrink-0">{z.emoji || (ZONE_KINDS.find((k) => k.id === z.kind) || {}).emoji || "📍"}</span>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate flex items-center gap-1.5">
                        {z.place}
                        {z.pos && <span className="text-[9px] font-bold uppercase rounded px-1 py-0.5 bg-[#0ea5e9]/15 text-[#0ea5e9]">pinned</span>}
                      </div>
                      {z.summary && <div className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">{z.summary}</div>}
                    </div>
                    {z.version != null && <span className="text-[10px] text-emerald-500 font-semibold shrink-0">v{z.version}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: live preview */}
        <div className="lg:sticky lg:top-6">
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4" data-testid="compiler-preview">
            <div className="flex items-center gap-2 mb-3 text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
              <Play className="w-3.5 h-3.5 text-[hsl(var(--primary))]" /> Live preview
              {running && <span className="text-[hsl(var(--primary))] normal-case font-medium">· updating…</span>}
            </div>
            {config ? <GameCanvas ref={canvasRef} config={config} compact previewMode onZonePin={handleZonePin} /> : <div className="aspect-video grid place-items-center"><Spinner /></div>}
            {pinMode && (
              <div className="mt-2 text-[11px] text-[#0ea5e9] font-medium text-center flex items-center justify-center gap-1" data-testid="compiler-pin-hint">
                <MapPin className="w-3 h-3" /> Tap the ground above to drop your zone pin
              </div>
            )}
            <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-3 text-center flex items-center justify-center gap-1">
              <ListChecks className="w-3 h-3" /> Every built system is saved as an undoable version.
            </p>
          </div>
          {/* Visual QA — the Autonomous Art Director looks at the live game */}
          <div className="mt-4">
            <VisualQA ref={visualQARef} gameId={gameId} capture={() => canvasRef.current?.capture()} onApplied={handleVisualApplied} />
          </div>
        </div>
      </div>

      {/* Production Board — the AI dev team's build log */}
      <ProductionBoard runs={runs} totals={boardTotals} openRun={openRun} setOpenRun={setOpenRun}
        confirmUndo={confirmUndo} setConfirmUndo={setConfirmUndo} onUndo={undoRun} undoing={undoing}
        onRedo={redoRun} redoing={redoing} />
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (isNaN(t)) return "";
  const s = Math.max(1, Math.round((Date.now() - t) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

const ProductionBoard = ({ runs, totals, openRun, setOpenRun, confirmUndo, setConfirmUndo, onUndo, undoing, onRedo, redoing }) => (
  <div className="mt-6 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5" data-testid="compiler-board">
    <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
      <div className="flex items-center gap-2 text-sm font-bold"><Activity className="w-4 h-4 text-[hsl(var(--primary))]" /> Production Board</div>
      {totals && (
        <div className="flex items-center gap-3 text-[11px] text-[hsl(var(--muted-foreground))]">
          <span data-testid="board-total-runs"><b className="text-[hsl(var(--foreground))]">{totals.runs}</b> runs</span>
          <span data-testid="board-total-built"><b className="text-emerald-500">{totals.systems_built}</b> systems built</span>
          <span data-testid="board-total-fixes"><b className="text-amber-500">{totals.quality_fixes}</b> quality fixes</span>
        </div>
      )}
    </div>

    {(!runs || runs.length === 0) ? (
      <p className="text-[12px] text-[hsl(var(--muted-foreground))] leading-snug">
        Your AI dev team's build log will appear here — every <b>Complete the Vision</b> run, the systems it built and the quality fixes it made, newest first.
      </p>
    ) : (
      <div className="space-y-2.5" data-testid="board-runs">
        {runs.map((r) => {
          const active = r.status === "processing";
          const after = r.depth_after?.overall;
          const isOpen = openRun === r.id;
          const built = r.steps ? r.steps.filter((s) => s.status === "built").length : (r.built || 0);
          return (
            <div key={r.id} className="rounded-xl border border-[hsl(var(--border))] overflow-hidden" data-testid={`board-run-${r.id}`}>
              <button onClick={() => setOpenRun(isOpen ? null : r.id)}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[hsl(var(--secondary))]/40 transition-colors">
                <span className={`w-2 h-2 rounded-full shrink-0 ${active ? "bg-[hsl(var(--primary))] animate-pulse" : r.status === "done" ? "bg-emerald-500" : "bg-amber-500"}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-sm font-semibold">{r.auto ? `Autopilot → ${r.target || 100}%` : "Complete the vision"}</span>
                    <span className="text-[9px] font-bold uppercase rounded px-1 py-0.5 bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))]">{r.mode}</span>
                    {r.deep && <span className="text-[9px] font-bold uppercase rounded px-1 py-0.5 bg-[#0ea5e9]/15 text-[#0ea5e9]">deep</span>}
                    {r.critic && <span className="text-[9px] font-bold uppercase rounded px-1 py-0.5 bg-amber-500/15 text-amber-500">QD</span>}
                    {r.playtesters && <span className="text-[9px] font-bold uppercase rounded px-1 py-0.5 bg-fuchsia-500/15 text-fuchsia-400">squad</span>}
                    {r.undone && <span className="text-[9px] font-bold uppercase rounded px-1 py-0.5 bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))]">undone</span>}
                    {active && <span className="text-[10px] text-[hsl(var(--primary))] font-medium">building…</span>}
                  </div>
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">
                    Built <b className="text-emerald-500">{built}</b>{r.passes > 1 ? ` · ${r.passes} passes` : ""} · {timeAgo(r.finished_at || r.created_at)}
                  </div>
                </div>
                {(r.depth_before != null && after != null) && (
                  <div className="text-right shrink-0">
                    <div className="text-sm font-bold">{r.depth_before}%<span className="text-[hsl(var(--muted-foreground))] font-normal mx-1">→</span><span className="text-[#0ea5e9]">{after}%</span></div>
                    {r.depth_after?.state && <div className="text-[10px] text-[hsl(var(--muted-foreground))]">{r.depth_after.state}</div>}
                  </div>
                )}
                <ChevronRight className={`w-4 h-4 shrink-0 text-[hsl(var(--muted-foreground))] transition-transform ${isOpen ? "rotate-90" : ""}`} />
              </button>
              {isOpen && (
                <div className="px-3 pb-3 pt-1 border-t border-[hsl(var(--border))] space-y-1.5" data-testid={`board-run-steps-${r.id}`}>
                  {(r.steps || []).length === 0 && <p className="text-[11px] text-[hsl(var(--muted-foreground))] pt-1.5">No systems built in this run.</p>}
                  {(r.steps || []).map((s, i) => (
                    <div key={i} className="flex items-start gap-2 text-[12px] pt-1.5">
                      {s.status === "built" ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />
                        : s.status === "failed" ? <XCircle className="w-3.5 h-3.5 text-rose-500 mt-0.5 shrink-0" />
                          : <MinusCircle className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))] mt-0.5 shrink-0" />}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-medium">{s.label}</span>
                          {s.engine === "deep" && <span className="text-[9px] font-bold uppercase rounded px-1 py-0.5 bg-[#0ea5e9]/15 text-[#0ea5e9]">deep</span>}
                          {s.version != null && <span className="text-[10px] text-emerald-500 font-semibold">v{s.version}</span>}
                          {s.critic?.fixed && <span className="text-[10px] text-amber-500 font-semibold">QD fix v{s.critic.fix_version}</span>}
                        </div>
                        {s.summary && <p className="text-[11px] text-[hsl(var(--muted-foreground))] leading-snug">{s.summary}</p>}
                        {s.critic?.verdict && <p className="text-[11px] leading-snug" style={{ color: (s.critic.score >= 70 ? "#10b981" : "#f59e0b") }}>{s.critic.mode === "playtest" ? "Playtesters" : "QD"} {s.critic.score}: {s.critic.verdict}</p>}
                        {Array.isArray(s.critic?.findings) && s.critic.findings.map((f, fi) => (
                          <p key={fi} className="text-[10px] text-[hsl(var(--muted-foreground))] leading-snug"><b>{f.persona}:</b> {f.note}</p>
                        ))}
                      </div>
                    </div>
                  ))}
                  {r.status !== "processing" && (
                    <div className="pt-2.5 border-t border-[hsl(var(--border))] mt-1.5">
                      {r.undone ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[11px] text-[hsl(var(--muted-foreground))] flex items-center gap-1"><RotateCcw className="w-3 h-3" /> This run was reverted to its baseline.</span>
                          {r.can_redo && (
                            <button className="inline-flex items-center gap-1 text-[11px] font-medium text-[hsl(var(--muted-foreground))] hover:text-[#0ea5e9] transition-colors disabled:opacity-50"
                              disabled={redoing} onClick={() => onRedo(r.id)} data-testid={`board-redo-${r.id}`}>
                              {redoing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Redo2 className="w-3 h-3" />} Redo this run
                            </button>
                          )}
                        </div>
                      ) : confirmUndo === r.id ? (
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] text-[hsl(var(--muted-foreground))]">Revert the game to before this run?</span>
                          <Button size="sm" variant="destructive" className="rounded-full h-6 px-2.5 text-[11px]" disabled={undoing} onClick={() => onUndo(r.id)} data-testid={`board-undo-confirm-${r.id}`}>
                            {undoing ? <Loader2 className="w-3 h-3 animate-spin" /> : "Yes, undo"}
                          </Button>
                          <button className="text-[11px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" onClick={() => setConfirmUndo(null)} data-testid={`board-undo-cancel-${r.id}`}>Cancel</button>
                        </div>
                      ) : (
                        <button className="inline-flex items-center gap-1 text-[11px] font-medium text-[hsl(var(--muted-foreground))] hover:text-rose-400 transition-colors"
                          onClick={() => setConfirmUndo(r.id)} data-testid={`board-undo-${r.id}`}>
                          <RotateCcw className="w-3 h-3" /> Undo this run
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    )}
  </div>
);

const Chip = ({ children }) => (
  <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold bg-[hsl(var(--secondary))] text-[hsl(var(--foreground))]">{children}</span>
);

const OptionRow = ({ icon: Icon, title, desc, checked, onChange, testid, disabled }) => (
  <div className="flex items-center gap-3">
    <div className="grid place-items-center w-7 h-7 rounded-lg shrink-0" style={{ background: checked ? "#0ea5e922" : "hsl(var(--secondary))" }}>
      <Icon className="w-4 h-4" style={{ color: checked ? "#0ea5e9" : "hsl(var(--muted-foreground))" }} />
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-sm font-semibold leading-tight">{title}</div>
      <div className="text-[11px] text-[hsl(var(--muted-foreground))] leading-snug">{desc}</div>
    </div>
    <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} data-testid={testid} />
  </div>
);
